import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import type { Approval, ApprovalType } from "@/types";

const TYPE_ICON: Record<ApprovalType, { symbol: string; bg: string; color: string }> = {
  transfer: { symbol: "↑", bg: "var(--ads-tint-blue)", color: "var(--ads-info)" },
  wallet: { symbol: "⊞", bg: "var(--ads-tint-green)", color: "var(--ads-viz-green)" },
  policy: { symbol: "⚙", bg: "var(--ads-warning-soft)", color: "var(--ads-viz-amber-strong)" },
};

function ApprovalRow({ approval }: { approval: Approval }) {
  const type = TYPE_ICON[approval.type];
  const href = approval.type === "policy" ? "/governance" : `/approvals/${approval.id}`;
  return (
    <Link
      to={href}
      className="flex items-start gap-3 rounded-lg border border-line bg-bg px-3 py-2 transition-colors hover:border-primary hover:bg-surface"
    >
      <span
        className="flex-shrink-0 rounded-[4px] px-2 py-[3px] text-[15px] font-bold leading-none"
        style={{ background: type.bg, color: type.color }}
      >
        {type.symbol}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-ink">{approval.title}</div>
        <div className="mt-0.5 truncate text-xs text-muted">{approval.meta}</div>
        <div className="mt-1.5 text-xs text-muted">{approval.hint}</div>
      </div>
      <span
        className={cn(
          "shrink-0 rounded-[4px] px-2 py-[3px] text-[11px] font-bold",
          approval.slaSeverity === "warn" || approval.slaSeverity === "danger"
            ? "bg-warning-soft text-warning"
            : "bg-success-soft text-success"
        )}
      >
        {approval.slaLeft}
      </span>
    </Link>
  );
}

export function PendingApprovals({ approvals }: { approvals: Approval[] }) {
  if (approvals.length === 0) {
    return (
      <div className="flex h-full flex-col">
        <h2 className="mb-4 text-sm font-bold uppercase tracking-[0.06em] text-muted">Pending approvals</h2>
        <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-line px-4 text-center text-sm text-muted">
          No pending approvals.
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-bold uppercase tracking-[0.06em] text-muted">Pending approvals</h2>
        <Link to="/approvals" className="text-xs font-semibold text-primary hover:underline">
          View all →
        </Link>
      </div>
      <div className="flex flex-1 flex-col justify-between gap-1.5">
        {approvals.map((a) => (
          <ApprovalRow key={a.id} approval={a} />
        ))}
      </div>
    </div>
  );
}
